/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useMemo, useState } from 'react';
import { Divider, View } from '@az/starc-ui';
import { ReviewImageCarousel } from '../ReviewImageCarousel/ReviewImageCarousel';
import { type ExtendedProductReviewImage } from '../../../../hooks/useProductReviewsImages';
import { type ProductReview } from '../../../../interface';
import { type ActiveImage } from '../../../../hooks/useReviewImageModal';
import type { Direction, CarouselProps } from '@/components/Carousel/types';
import { type ThumbnailProps } from '@/components/Carousel/ThumbnailComponent/types';
import { NEXT, PREV } from '@/components/Carousel/constants';
import styles from './styles.module.scss';
import { Review } from '../Review/Review';
import { useMediaQuery } from '@/hooks/useMediaQuery';

export type DetailsViewProps = {
  image: ActiveImage;
  images: ExtendedProductReviewImage[];
  review: ProductReview | undefined;
  onImageChange: (image: ActiveImage, hasBackButton?: boolean) => void;
};

export const DetailsView = ({ image, images, review, onImageChange }: DetailsViewProps) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up('lg'));

  const selectedThumbnailIndex = useMemo(() => {
    return review?.images.findIndex((item) => item.id === image?.imageId);
  }, [image?.imageId, review?.images]);

  const [direction, setDirection] = useState<Direction>();

  const changeReview: CarouselProps['onSlide'] = (direction) => {
    if (!review) return;

    const endOfSet = selectedThumbnailIndex === review.images.length - 1;
    const startOfSet = selectedThumbnailIndex === 0;
    const isNext = direction === NEXT && endOfSet;
    const isPrev = direction === PREV && startOfSet;

    if (!isNext && !isPrev) return;

    const index = images.findIndex((img) => img.id === image?.imageId);

    let nextImage: ExtendedProductReviewImage | null = null;
    if (isNext) {
      const isLastImageOfAllReviews = index === images.length - 1;
      const nextReviewImageIndex = isLastImageOfAllReviews ? 0 : index + 1;
      nextImage = images[nextReviewImageIndex];
      setDirection(NEXT);
    }

    if (isPrev) {
      const isFistImageOfAllReviews = index === 0;
      const prevReviewImageIndex = isFistImageOfAllReviews ? images.length - 1 : index - 1;
      nextImage = images[prevReviewImageIndex];
      setDirection(PREV);
    }

    if (nextImage) {
      onImageChange({ imageId: nextImage.id, reviewId: nextImage.reviewID });
    }
  };

  const changeImage: ThumbnailProps['onImageChange'] = (index) => {
    const nextImage = review?.images[index];

    if (nextImage) {
      onImageChange({ imageId: nextImage.id, reviewId: review.id });
    }
  };

  return (
    <View className={styles.root} attributes={{ 'data-testid': 'details-view' }}>
      <View
        direction={{ l: 'row' }}
        height={{ l: '100%' }}
        align={{ s: 'center', m: 'center', l: 'start' }}
      >
        <View.Item columns={{ s: 12, m: 12, l: 6 }} className={styles.carouselContainer}>
          <ReviewImageCarousel
            key={review?.id}
            slideAnimationDirection={direction}
            selectedThumbnailIndex={selectedThumbnailIndex ?? 0}
            images={review?.images ?? []}
            onImageChange={changeImage}
            onSlide={changeReview}
          />
        </View.Item>

        {!isDesktop ? (
          <View.Item className={styles.dividerContainer}>
            <Divider className={styles.divider} color="300" />
          </View.Item>
        ) : null}

        <View.Item
          columns={{ s: 12, m: 12, l: 6 }}
          gapBefore={{ l: 8 }}
          className={styles.reviewContainer}
        >
          {review ? <Review review={review} /> : null}
        </View.Item>
      </View>
    </View>
  );
};
